"use client";

import {
  DatePickerPortal,
  GuestCounter,
  PortalDropdown,
} from "@/components/shared/main/main-fun";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { RiMapPin2Line, RiSearchLine, RiUser3Line } from "react-icons/ri";

export interface GuestCounts {
  adults: number;
  children: number;
}

export default function NavSearchBar() {
  const t = useTranslations("search");
  const locale = useLocale();
  const isRTL = locale === "ar";
  const router = useRouter();

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showGuests, setShowGuests] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [guests, setGuests] = useState<GuestCounts>({ adults: 1, children: 0 });

  const guestRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLDivElement>(null);

  const totalGuests = guests.adults + guests.children;

  const handleSearch = () => {
    const params = new URLSearchParams();
    const q = locationInput.trim();
    if (q) params.set("search", q);
    if (startDate) params.set("arrival_date", format(startDate, "yyyy-MM-dd"));
    if (endDate) params.set("departure_date", format(endDate, "yyyy-MM-dd"));
    params.set("adults", guests.adults.toString());
    if (guests.children > 0) params.set("children", guests.children.toString());
    router.push(`/${locale}/explore?${params.toString()}`);
  };

  // ── Close guests dropdown on outside click ────────────────────────────────
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (guestRef.current && !guestRef.current.contains(e.target as Node))
        setShowGuests(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="flex items-center h-12 bg-white dark:bg-zinc-900 rounded-full border border-neutral-200 dark:border-zinc-700 divide-x divide-neutral-200 dark:divide-zinc-700 w-full"
      style={isRTL ? { direction: "rtl" } : undefined}
    >
      {/* ── 1. Location — plain text input ───────────────────────────── */}
      <div className="relative flex items-center gap-2.5 px-5 h-full flex-[1.6] min-w-0 rounded-s-full">
        <RiMapPin2Line size={16} className="text-[var(--brand)] shrink-0" />
        <input
          type="text"
          value={locationInput}
          onChange={(e) => setLocationInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={t("whereToStay")}
          className={`w-full bg-transparent text-[13px] font-medium text-neutral-800 dark:text-white placeholder:font-normal placeholder:text-neutral-500 outline-none ${
            isRTL ? "text-right" : ""
          }`}
          dir={isRTL ? "rtl" : "ltr"}
        />
      </div>

      {/* ── 2. Dates ──────────────────────────────────────────────── */}
      <div
        ref={dateRef}
        className="relative flex items-center gap-2.5 px-5 h-full flex-2 min-w-0"
      >
        <DatePickerPortal
          anchorRef={dateRef}
          isRTL={isRTL}
          startDate={startDate}
          endDate={endDate}
          onChangeStart={setStartDate}
          onChangeEnd={setEndDate}
          placeholderCheckin={t("checkin")}
          placeholderCheckout={t("checkout")}
        />
      </div>

      {/* ── 3. Guests ─────────────────────────────────────────────── */}
      <div
        ref={guestRef}
        className="relative flex items-center gap-2.5 px-5 h-full cursor-pointer hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors flex-[0.9] min-w-0"
        onClick={() => setShowGuests((v) => !v)}
      >
        <RiUser3Line size={16} className="text-neutral-400 shrink-0" />
        <span className="text-[13px] font-medium text-neutral-800 dark:text-white whitespace-nowrap">
          {totalGuests} {totalGuests === 1 ? t("guest") : t("guests")}
        </span>

        <AnimatePresence>
          {showGuests && (
            <PortalDropdown anchorRef={guestRef} isRTL={isRTL}>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white dark:bg-zinc-900 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.13)] border border-neutral-100 dark:border-zinc-700 p-5 min-w-65"
              >
                <GuestCounter
                  label={t("adults")}
                  subtitle={t("adultsAge")}
                  value={guests.adults}
                  onChange={(v) => setGuests((g) => ({ ...g, adults: v }))}
                  min={1}
                />
                <div className="border-t border-neutral-100 dark:border-zinc-700 my-1" />
                <GuestCounter
                  label={t("children")}
                  subtitle={t("childrenAge")}
                  value={guests.children}
                  onChange={(v) => setGuests((g) => ({ ...g, children: v }))}
                />
              </motion.div>
            </PortalDropdown>
          )}
        </AnimatePresence>
      </div>

      {/* ── 4. Search Button ──────────────────────────────────────── */}
      <div className="flex items-center px-2 h-full shrink-0">
        <button
          onClick={handleSearch}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-[var(--brand)] hover:bg-[var(--brand-dark)] active:scale-95 transition-all shrink-0 cursor-pointer"
          aria-label="search"
        >
          <RiSearchLine size={16} className="text-white" />
        </button>
      </div>
    </div>
  );
}
